<script setup lang="ts">
import { defineProps, computed, getCurrentInstance } from 'vue';

import { isArticleOption } from '@/library/message/lib';
import { PredefinedOptionType, SystemEvent, SystemOptionType } from '@/library/message/enum';
import type {
  ArticleListNavigationEvent,
  ArticleListWithOtherNavigationMessage,
  ArticleSystemOption,
} from '@/library/message/types';

import ChatMessageWrapper from './ChatMessageWrapper.vue';
import MessageActions from '../MessageActions.vue';
import type { ActionItem } from '../MessageActions.vue';
import { useI18n } from '@/plugins/i18n';

const props = defineProps<{
  /**
   * The previous message/notice in the stack.
   */
  previous?: unknown;

  /**
   * The next message/notice in the stack.
   */
  next?: unknown;

  message: ArticleListWithOtherNavigationMessage;
}>();

const emit = defineEmits<{
  (e: 'message:input', event: ArticleListNavigationEvent): void;
}>();

const { t } = useI18n();

const articleOptions = computed<ArticleSystemOption[]>(() => {
  const options = props.message.options.filter(isArticleOption) as ArticleSystemOption[];

  if (!props.message.elements?.issueNotListed) return options;

  return [
    ...options,
    {
      id: PredefinedOptionType.ISSUE_NOT_LISTED,
      kind: SystemOptionType.ARTICLE,
      title: getCurrentInstance()?.proxy.$t('chat.sys.msg.issue_not_listed', 'My issue is not listed here.') as string,
      checks: 'DIRTY_ELEMENT_TO_PREVENT_ICON_FROM_SHOWING_UP',
    },
  ];
});

const elements = computed(() => props.message.elements || {});

const emitSelection = (selected: ArticleListNavigationEvent['payload']['selected']) => {
  const event: ArticleListNavigationEvent = {
    type: SystemEvent.ARTICLE_LIST_NAVIGATION,
    message: props.message,
    payload: { selected },
  };

  emit('message:input', event);
};

const buildArticleSelectedEvent = (article: ArticleSystemOption) => {
  const selected =
    article.id === PredefinedOptionType.ISSUE_NOT_LISTED ? PredefinedOptionType.ISSUE_NOT_LISTED : { ...article };
  emitSelection(selected);
};

const buildBrowserOtherArticlesSelected = () => {
  emitSelection(PredefinedOptionType.BROWSE_OTHER_ARTICLES);
};

const selectedOptionId = computed(() =>
  typeof props.message.selectedOption === 'string' ? props.message.selectedOption : props.message.selectedOption?.id
);

const articleActionItems = computed<ActionItem[]>(() =>
  articleOptions.value.map((article) => ({
    id: article.id,
    label: article.title,
    rightIcon: article.checks ? undefined : 'FaChevronRight',
    selected: !!(props.message.selectedOption && article.id === selectedOptionId.value),
  }))
);

const browseArticlesActionItems = computed<ActionItem[]>(() => [
  {
    id: PredefinedOptionType.BROWSE_OTHER_ARTICLES,
    label: String(t('chat.sys.msg.browse_articles')),
    icon: 'BiInfoCircle',
    selected: PredefinedOptionType.BROWSE_OTHER_ARTICLES === props.message.selectedOption,
  },
]);

const handleArticleSelect = (item: ActionItem) => {
  const article = articleOptions.value.find((a) => a.id === item.id);
  if (article) {
    buildArticleSelectedEvent(article);
  }
};

const handleBrowseArticlesSelect = () => {
  buildBrowserOtherArticlesSelected();
};
</script>

<template>
  <ChatMessageWrapper :previous="previous" :next="next" :message="message">
    <template #actions>
      <MessageActions :items="articleActionItems" @select="handleArticleSelect" />

      <template v-if="elements.browseArticles">
        <MessageActions
          container-class="browse-articles-actions"
          :items="browseArticlesActionItems"
          @select="handleBrowseArticlesSelect"
        />
      </template>
    </template>
  </ChatMessageWrapper>
</template>

<style scoped>
.item {
  justify-content: space-between;
}
</style>

<style>
.browse-articles-actions .item.item.item {
  margin-top: 0;
}
</style>
